import { defaultPayloadFormat } from './aiModelConstants';
import { logAiModel } from './aiModelLogger';
import { buildSampleRequestObject, buildSampleResponseObject, prettyJson } from './aiModelSchema';
import {
  AiModelClassificationLabel,
  AiModelFormState,
  AiModelInputField,
  AiModelOutputField,
  AiRegisteredModel,
} from './aiModelTypes';
import { emptyCredentialMask, emptyCredentials } from './aiModelService';

export function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function slugifyCode(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function emptyInputField(partial?: Partial<AiModelInputField>): AiModelInputField {
  return {
    id: newId(),
    name: '',
    dataType: 'string',
    required: true,
    nullable: false,
    example: '',
    ...partial,
  };
}

export function emptyOutputField(partial?: Partial<AiModelOutputField>): AiModelOutputField {
  return {
    id: newId(),
    name: '',
    dataType: 'string',
    scoreSupported: false,
    ...partial,
  };
}

export function emptyLabel(partial?: Partial<AiModelClassificationLabel>): AiModelClassificationLabel {
  return {
    id: newId(),
    value: '',
    label: '',
    ...partial,
  };
}

export function createDefaultForm(): AiModelFormState {
  return {
    name: '',
    code: '',
    version: 'v1.0',
    description: '',
    environment: 'development',
    tags: [],
    owner: '',
    provider: 'rest',
    endpointId: '',
    location: '',
    connectionId: '',
    authLabel: '',
    httpMethod: 'POST',
    contentType: 'application/json',
    payloadFormat: defaultPayloadFormat('rest'),
    timeoutMs: 5000,
    retryCount: 0,
    task: 'binary_classification',
    modality: 'tabular',
    inputFields: [emptyInputField()],
    outputFields: [
      emptyOutputField({ name: 'prediction', dataType: 'category' }),
      emptyOutputField({ name: 'score', dataType: 'number', scoreSupported: true }),
    ],
    classificationLabels: [
      emptyLabel({ value: '0', label: 'Legitimate' }),
      emptyLabel({ value: '1', label: 'Fraud' }),
    ],
    preprocessingIncluded: true,
    preprocessingNotes: '',
    inferenceMode: 'realtime',
    sampleRequest: '{\n  \n}',
    sampleResponse: '{\n  \n}',
    framework: '',
    algorithm: '',
    components: [],
    credentials: emptyCredentials(),
    credentialsConfigured: false,
    credentialHints: emptyCredentialMask(),
  };
}

export function modelToForm(model: AiRegisteredModel): AiModelFormState {
  return {
    name: model.name,
    code: model.code,
    version: model.version,
    description: model.description,
    environment: model.environment,
    tags: [...model.tags],
    owner: model.owner,
    provider: model.provider,
    endpointId: model.endpointId,
    location: model.location,
    connectionId: model.connectionId,
    authLabel: model.authLabel,
    httpMethod: model.httpMethod,
    contentType: model.contentType,
    payloadFormat: model.payloadFormat,
    timeoutMs: model.timeoutMs,
    retryCount: model.retryCount,
    task: model.task,
    modality: model.modality,
    inputFields: model.inputFields.map((field) => ({ ...field })),
    outputFields: model.outputFields.map((field) => ({ ...field })),
    classificationLabels: model.classificationLabels.map((row) => ({ ...row })),
    preprocessingIncluded: model.preprocessingIncluded,
    preprocessingNotes: model.preprocessingNotes,
    inferenceMode: model.inferenceMode,
    sampleRequest: model.sampleRequest,
    sampleResponse: model.sampleResponse,
    framework: model.framework,
    algorithm: model.algorithm,
    components: [...model.components],
    lastTest: model.lastTest ? { ...model.lastTest, checks: [...model.lastTest.checks] } : undefined,
    credentials: emptyCredentials(),
    credentialsConfigured: model.credentialsConfigured,
    credentialHints: model.credentials,
  };
}

export function regenerateExamples(form: AiModelFormState): Pick<AiModelFormState, 'sampleRequest' | 'sampleResponse'> {
  return {
    sampleRequest: prettyJson(buildSampleRequestObject(form.inputFields)),
    sampleResponse: prettyJson(buildSampleResponseObject(form.outputFields, form.classificationLabels)),
  };
}

export function parseTags(text: string): string[] {
  return text
    .split(/,|\n/)
    .map((tag) => tag.trim())
    .filter(Boolean);
}

export type StepErrors = Record<string, string>;

export function validateStep(stepIndex: number, form: AiModelFormState): StepErrors {
  const errors: StepErrors = {};
  const namedInputs = form.inputFields.filter((field) => field.name.trim());
  const namedOutputs = form.outputFields.filter((field) => field.name.trim());

  if (stepIndex === 0) {
    if (!form.name.trim()) errors.name = 'Name is required.';
    if (!form.code.trim()) errors.code = 'Code is required.';
    if (!form.version.trim()) errors.version = 'Version is required.';
  }
  if (stepIndex === 1) {
    if (!form.endpointId.trim()) errors.endpointId = 'Model / endpoint ID is required.';
    if (!form.location.trim()) errors.location = 'Location is required.';
    if (form.timeoutMs < 1) errors.timeoutMs = 'Timeout must be at least 1 ms.';
    const creds = form.credentials;
    const configured = form.credentialsConfigured;
    if (form.provider === 'huggingface' && !creds.hfToken.trim() && !configured) {
      errors.hfToken = 'Hugging Face token is required.';
    }
    if (form.provider === 'azure_ml') {
      if (!creds.apiKey.trim() && !configured) errors.apiKey = 'Azure API key is required.';
      if (!creds.scoringUri.trim() && !form.credentialHints.scoringUri.set) {
        errors.scoringUri = 'Azure scoring URI is required.';
      }
    }
    if (form.provider === 'sagemaker') {
      if (!creds.awsAccessKeyId.trim() && !form.credentialHints.awsAccessKeyId.set) {
        errors.awsAccessKeyId = 'AWS access key is required.';
      }
      if (!creds.awsSecretAccessKey.trim() && !configured) {
        errors.awsSecretAccessKey = 'AWS secret key is required.';
      }
    }
    if (form.provider === 'databricks' && !creds.databricksToken.trim() && !configured) {
      errors.databricksToken = 'Databricks token is required.';
    }
    if (
      (form.provider === 'rest' || form.provider === 'on_prem' || form.provider === 'mlflow' || form.provider === 'vertex_ai') &&
      !creds.bearerToken.trim() &&
      !creds.apiKey.trim() &&
      !configured
    ) {
      errors.bearerToken = 'Bearer token or API key is required.';
    }
  }
  if (stepIndex === 3) {
    if (namedInputs.length === 0) errors.inputFields = 'Add at least one input field.';
  }
  if (stepIndex === 4) {
    if (namedOutputs.length === 0) errors.outputFields = 'Add at least one output field.';
  }
  if (stepIndex === 5 && !form.preprocessingIncluded && !form.preprocessingNotes.trim()) {
    errors.preprocessingNotes = 'Describe required preprocessing when it is not included in the deployment.';
  }
  if (stepIndex === 7) {
    try {
      JSON.parse(form.sampleRequest);
    } catch {
      errors.sampleRequest = 'Sample request must be valid JSON.';
    }
    try {
      JSON.parse(form.sampleResponse);
    } catch {
      errors.sampleResponse = 'Sample response must be valid JSON.';
    }
  }
  return errors;
}

export function validateForSave(form: AiModelFormState): StepErrors {
  return {
    ...validateStep(0, form),
    ...validateStep(1, form),
    ...validateStep(3, form),
    ...validateStep(4, form),
    ...validateStep(5, form),
    ...validateStep(7, form),
  };
}

export function firstErrorMessage(errors: StepErrors): string | null {
  const first = Object.values(errors)[0];
  return first ?? null;
}

export function logFormWarning(event: string, extra?: Record<string, unknown>): void {
  logAiModel('warning', event, extra);
}
